import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { app, go, leaveChildMode } from '../state/app';
import { getVideos, playbackUrls } from '../api/endpoints';
import { Player } from '../components/Player';
import type { Video } from '../api/types';

/**
 * Child mode: the app the toddler actually holds.
 *
 * The child is NOT a principal. They have no login — they are holding the parent's
 * device with the parent's session, which means no server-side check can tell
 * the two of them apart on this surface. That is the design, not a hole in it:
 *
 *   * the REAL gate is server-side and already happened — a video only becomes
 *     `published` when a parent approves it, and playback URLs re-check that
 *     on every mint, so revoking approval takes effect immediately.
 *   * this screen is the presentation of that decision: it renders only
 *     published videos, and offers nothing else to touch.
 *   * the actual boundary is the PIN on the way BACK to parent mode. Getting
 *     OUT is what needs guarding; getting in does not.
 *
 * A second device (the child's own tablet) is a different story with real
 * server-side enforcement — it pairs with an opaque device token whose
 * `childOnly` flag is stamped by the device row and cannot be asked for by the
 * caller. This screen is the primary, one-device case.
 */

/**
 * One array, reused, for "we are opening a film but do not have its playlist
 * yet". The Player treats a new `urls` identity as a new film and resets
 * itself, so a fresh [] on every render would wipe what it is holding between
 * the tap and the playlist landing. Belt and braces rather than a fix for an
 * observed bug: nothing re-renders this screen inside that window today, which
 * is why no test can catch this constant being inlined.
 */
const NO_PLAYLIST: string[] = [];

export function ChildMode() {
  const { childPin } = app.use();
  const [videos, setVideos] = useState<Video[] | null>(null);
  /** The shelf could not be loaded. Playback failures are `playFailed`. */
  const [loadFailed, setLoadFailed] = useState(false);
  const [playing, setPlaying] = useState<Video | null>(null);
  const [urls, setUrls] = useState<string[] | null>(null);
  const [playFailed, setPlayFailed] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let alive = true;
    getVideos()
      .then((response) => {
        if (!alive) return;
        // Filter here as well as trusting the server: this surface must never
        // show an unapproved episode even for the instant between a revoke and
        // the next refresh.
        setVideos((response.videos ?? []).filter((v) => v.status === 'published'));
      })
      .catch(() => alive && setLoadFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!playing) {
      setUrls(null);
      setPlayFailed(false);
      return;
    }
    let alive = true;
    playbackUrls(playing.videoId)
      .then((response) => {
        if (!alive) return;
        const segments = (response.segmentUrls ?? []).filter(Boolean);
        setUrls(segments.length ? segments : response.hlsUrl ? [response.hlsUrl] : []);
      })
      .catch(() => alive && setPlayFailed(true));
    return () => {
      alive = false;
    };
  }, [playing]);

  if (playing) {
    // THE PLAYER IS MOUNTED FROM THE FIRST FRAME OF THIS BRANCH, before there
    // is anything to play. It used to be rendered only once the playlist had
    // been minted, and that ordering is what cost the second tap: the tile tap
    // is the user gesture, the <video> elements did not exist while it was
    // happening, and iOS Safari hands out playback permission per element per
    // gesture. Mounting them here — inside the tap, see the tile's onClick —
    // lets the Player claim that permission and start on its own when the URLs
    // land. See components/Player.tsx for what "claim" means.
    //
    // THE ONE-WAY DOOR, and it was one. The loading state rendered a bare "…"
    // and nothing else, on the only surface in the app with no top bar and no
    // back button — so a film that could not be played left a two-year-old
    // holding a screen with literally zero controls on it. Both ways in are
    // ordinary: playback-urls resolves to no playable segment when an episode
    // has no stitched rendition, and it 403s when the parent revoked approval
    // between the shelf painting and the tile being tapped.
    //
    // Nothing here is readable by the audience, so the way out is a big glyph
    // button in the same place the player keeps its close control, and it is
    // rendered while loading too: a slow mint is indistinguishable from a stuck
    // one to someone who cannot read, and a second of a visible exit costs less
    // than a minute of a trapped child. It is an OPAQUE overlay rather than an
    // alternative to the player, because the player underneath it already has a
    // × of its own and two exits in one corner is a worse screen than one.
    const ready = Boolean(urls && urls.length > 0);
    const stuck = playFailed || (urls !== null && urls.length === 0);
    return (
      <div className="child-player">
        <Player
          urls={ready ? urls! : NO_PLAYLIST}
          autoStart
          onExit={() => setPlaying(null)}
          startLabel="▶︎ Watch"
        />
        {!ready && (
          <div className="child-loading">
            <div className="child-stuck-face">{stuck ? '🙈' : '…'}</div>
            <button
              className="child-back"
              aria-label="back to the films"
              onClick={() => setPlaying(null)}
            >
              ‹
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="child">
      <div className="child-head">
        <h1>Pick a film</h1>
        {/* Small, plain, and in the corner: an exit the child is not invited
            to press, but that a parent can always find. */}
        <button className="child-exit" onClick={() => setExiting(true)} aria-label="parent mode">
          ⚙
        </button>
      </div>

      {/* One sentence for both "the shelf is empty" and "the shelf would not
          load", because to a two-year-old they are the same event and the
          remedy is the same person. The API's own words used to be printed here
          in an alarm-coloured box — "not approved", "HTTP 502" — which is a
          error message aimed at nobody: the audience cannot read, and the
          parent is in another room. The dev panel still logs every call. */}
      {(loadFailed || (videos && videos.length === 0)) && (
        <div className="child-empty">Nothing here yet — ask a grown-up!</div>
      )}

      {/* Pictures, not labels. The audience cannot read, so a title under a
          coloured rectangle is not navigation — it is decoration on a guess. */}
      <div className="child-grid">
        {(videos ?? []).map((video) => (
          <button
            key={video.videoId}
            className="child-tile"
            /* No visible text — the child cannot read it. But the tile still
               needs a NAME for screen readers, and it is what tests assert on
               instead of pixels. */
            aria-label={video.title || 'A film'}
            /* flushSync IS THE ONE-TAP FIX AND IS NOT DECORATION. React 18
               commits a click's state change in a microtask, by which time
               WebKit has torn down the user-gesture that the tap created.
               Forcing the commit here mounts the Player — and runs its
               mount-time layout effect, which calls play() on both <video>
               elements — while this handler is still on the stack, so the
               gesture is spent on the elements that are about to show the
               film. Take it out and the film needs a second tap again. */
            onClick={() => flushSync(() => setPlaying(video))}
          >
            {video.posterUrl ? (
              <img src={video.posterUrl} alt="" />
            ) : (
              <div className="child-tile-blank">🎬</div>
            )}
            <span className="child-play">▶</span>
          </button>
        ))}
      </div>

      {exiting && <PinGate onCancel={() => setExiting(false)} pin={childPin} />}
    </div>
  );
}

/**
 * The way back to parent mode.
 *
 * Deliberately a real check rather than a confirm dialog: the whole point of
 * handing over the device is that the parent can stop watching it. It is not a
 * cryptographic boundary — anything on the device is reachable by someone who
 * knows what they are doing — it is a boundary against a two-year-old, which
 * is exactly the threat model. Apple requires an equivalent gate for the Kids
 * category anyway.
 */
function PinGate({ pin, onCancel }: { pin: string | null; onCancel: () => void }) {
  const [entered, setEntered] = useState('');
  const [wrong, setWrong] = useState(false);

  const press = (digit: string) => {
    const next = entered + digit;
    if (next.length < 4) {
      setEntered(next);
      return;
    }
    if (next === pin) {
      // Drops the stored lock as well as the flag: leave only the flag and the
      // next reload puts the parent back behind their own PIN pad.
      leaveChildMode();
      go('gallery');
      return;
    }
    setWrong(true);
    setEntered('');
    window.setTimeout(() => setWrong(false), 600);
  };

  return (
    <div className="pin-backdrop" onClick={onCancel}>
      <div className={`pin${wrong ? ' pin-wrong' : ''}`} onClick={(e) => e.stopPropagation()}>
        <p>Grown-ups only</p>
        <div className="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={i < entered.length ? 'on' : ''} />
          ))}
        </div>
        <div className="pin-pad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) =>
            key === '' ? (
              <span key={i} />
            ) : (
              <button
                key={i}
                onClick={() => (key === '⌫' ? setEntered(entered.slice(0, -1)) : press(key))}
              >
                {key}
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
